// src/components/DisputesAdmin.js
// Payment Settings "Disputes" (admin only; STEP3_5A_TIERS_DESIGN.md 3.13.4,
// 3.13.5 and 5.14; release R6).
//
// Four parts, each on its own callable:
//  - the list (adminListDisputes): open Stripe disputes with the reason,
//    amount, due date, chips (issuer fraud warning, bank confirmed with 3D
//    Secure, member activity after the charge, never live watching) and the
//    suggested action; and the fraud warnings that were not refunded
//    automatically, with why (the reason chip) and "Verified account, no
//    hold" when that applies.
//  - per dispute: "Build packet" (adminBuildDisputeEvidence shows the draft),
//    "Save draft to Stripe", "Submit to Stripe" and "Accept"
//    (adminDisputeAction draft / submit / accept); per warning: "Refund",
//    "Refund as fraud" and "Apply hold" (efw_refund / efw_refund_fraud /
//    apply_hold). Submit, Accept and every warning action ask first, in
//    5.14's words. The founder clicks; the server runs on its own keys.
//    A warning the webhook never finished (R6 review R6-DARK-3) offers
//    "Handle now" (efw_import: the webhook's own handler, run on it).
//    A dispute that takes no response shows its status (R6 review R6C-5).
//  - three settings blocks, each saved on its own with a note through
//    adminSetTipLimitConfig's partial updates (no mode): "Issuer fraud
//    warnings" ({efw}), "Ask the bank to confirm (3D Secure)" ({threeDS})
//    and "Membership joins" ({join}). The stored values arrive with
//    adminListTipReviewShares (config), the same read Purchase limits uses.
//
// The 3D Secure scope is set only here (Purchase limits never sends it).

import React, { useCallback, useEffect, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Switch, TextInput,
  Alert, Platform,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  DISPUTES_COPY as C,
  SCOPES,
  FLOWS,
  fmtUsd,
  normalizeDisputeList,
  disputeRowLine,
  disputeChips,
  warningRowLine,
  warningChips,
  warningActions,
  hasOpenItems,
  suggestionLine,
  settingsDrafts,
  efwPayload,
  threeDSPayload,
  joinPayload,
} from '../utils/disputesAdmin';

const COLORS = {
  CARD: '#111',
  GOLD: '#FCD000',
  TEXT: '#fff',
  DIM: '#888',
  BORDER: '#222',
  DANGER: '#ff4444',
  WARN: '#f5c542',
};

function confirmAsk(message) {
  if (Platform.OS === 'web') return Promise.resolve(typeof window !== 'undefined' ? window.confirm(message) : false);
  return new Promise((resolve) => {
    Alert.alert(C.title, message, [
      { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
      { text: 'OK', onPress: () => resolve(true) },
    ]);
  });
}

function showProblem(msg) {
  if (Platform.OS === 'web') { if (typeof window !== 'undefined') window.alert('Error: ' + msg); return; }
  Alert.alert('Error', msg);
}

export default function DisputesAdmin() {
  const functions = getFunctions();
  const [list, setList] = useState(null);         // normalizeDisputeList(adminListDisputes)
  const [listError, setListError] = useState(null);
  const [config, setConfig] = useState(null);     // configOut (adminListTipReviewShares.config)
  const [configError, setConfigError] = useState(null);
  const [drafts, setDrafts] = useState(null);     // settingsDrafts(config)
  const [saving, setSaving] = useState(null);     // 'efw' | 'threeDS' | 'join'
  const [busy, setBusy] = useState(null);         // the dispute or warning id in flight
  const [packets, setPackets] = useState({});     // disputeId -> adminBuildDisputeEvidence reply

  const loadList = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'adminListDisputes');
      const result = await fn({});
      setList(normalizeDisputeList(result.data));
      setListError(null);
    } catch (e) {
      console.warn('[disputes] list fetch failed:', e.message);
      setListError(e.message || 'Could not load.');
    }
  }, [functions]);

  const loadConfig = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'adminListTipReviewShares');
      const result = await fn({});
      const cfg = result.data?.config || null;
      setConfig(cfg);
      setDrafts(settingsDrafts(cfg));
      setConfigError(null);
    } catch (e) {
      console.warn('[disputes] config fetch failed:', e.message);
      setConfigError(e.message || 'Could not load.');
    }
  }, [functions]);

  useEffect(() => {
    loadList();
    loadConfig();
  }, [loadList, loadConfig]);

  const setDraft = (block, patch) => setDrafts((cur) => {
    const base = cur || settingsDrafts(config);
    return { ...base, [block]: { ...base[block], ...patch } };
  });

  // One block, saved on its own (a partial update; the server writes the
  // block whole and one audit row).
  const saveBlock = async (block) => {
    if (!drafts || saving) return;
    const payload = block === 'efw' ? efwPayload(drafts.efw)
      : block === 'threeDS' ? threeDSPayload(drafts.threeDS)
        : joinPayload(drafts.join);
    if (!payload.note) { showProblem('A note is required.'); return; }
    setSaving(block);
    try {
      const fn = httpsCallable(functions, 'adminSetTipLimitConfig');
      const result = await fn(payload);
      const cfg = result.data || null;
      setConfig(cfg);
      // The saved block from the server; the other drafts keep their edits.
      setDrafts((cur) => ({ ...(cur || {}), [block]: settingsDrafts(cfg)[block] }));
    } catch (e) {
      showProblem(e.message);
    } finally {
      setSaving(null);
    }
  };

  // The list's mode, so an action on a dispute reaches the same Stripe mode
  // the list was read from (a warning carries its own mode on the server).
  const modeArg = () => (typeof list?.testMode === 'boolean' ? { testMode: list.testMode } : {});

  // Every action but "Save draft to Stripe" asks first (5.14) and carries
  // confirm: true, which the server requires.
  const act = async (target, action, message) => {
    if (busy) return;
    if (message && !(await confirmAsk(message))) return;
    const id = target.disputeId || target.efwId;
    setBusy(id);
    try {
      const fn = httpsCallable(functions, 'adminDisputeAction');
      await fn({ ...target, ...(target.disputeId ? modeArg() : {}), action, ...(message ? { confirm: true } : {}) });
      await loadList();
    } catch (e) {
      showProblem(e.message);
    } finally {
      setBusy(null);
    }
  };

  const buildPacket = async (dp) => {
    if (busy) return;
    setBusy(dp.id);
    try {
      const fn = httpsCallable(functions, 'adminBuildDisputeEvidence');
      const result = await fn({ disputeId: dp.id, ...modeArg() });
      setPackets((p) => ({ ...p, [dp.id]: result.data || {} }));
    } catch (e) {
      showProblem(e.message);
    } finally {
      setBusy(null);
    }
  };

  // ---------------------------------------------------------------- pieces
  const chipRow = (chips) => (chips.length ? (
    <View style={styles.chips}>
      {chips.map((c) => <Text key={c} style={styles.chip}>{c}</Text>)}
    </View>
  ) : null);

  const smallBtn = (label, onPress, { danger = false, disabled = false } = {}) => (
    <TouchableOpacity
      key={label}
      style={[danger ? styles.smallBtnDanger : styles.smallBtn, disabled && styles.btnDisabled]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
    >
      <Text style={danger ? styles.smallBtnDangerText : styles.smallBtnText}>{label}</Text>
    </TouchableOpacity>
  );

  const renderPacket = (dp) => {
    const pk = packets[dp.id];
    if (!pk) return null;
    const ev = pk.evidence && typeof pk.evidence === 'object' ? pk.evidence : {};
    const fields = Object.keys(ev).filter((k) => typeof ev[k] === 'string' && ev[k]);
    const notes = (Array.isArray(pk.notes) ? pk.notes : []).filter((n) => typeof n === 'string' && n);
    return (
      <View style={styles.packet}>
        {fields.map((k) => (
          <View key={k} style={styles.packetField}>
            <Text style={styles.packetKey}>{k}</Text>
            <Text style={styles.packetVal}>{ev[k]}</Text>
          </View>
        ))}
        {notes.map((n) => <Text key={n} style={styles.warnText}>{n}</Text>)}
      </View>
    );
  };

  const renderDispute = (dp) => {
    const amount = fmtUsd(dp.amountCents);
    const suggestion = suggestionLine(dp.suggestion);
    const off = busy === dp.id;
    return (
      <View key={dp.id} style={styles.rowWrap}>
        <View style={styles.row}>
          <Text style={styles.rowName}>{disputeRowLine(dp)}</Text>
          {chipRow(disputeChips(dp))}
          {suggestion ? <Text style={styles.rowMeta}>{suggestion}</Text> : null}
          {dp.purchaseLabel ? <Text style={styles.rowMeta}>{dp.purchaseLabel}</Text> : null}
          <Text style={styles.rowKey}>{dp.id}</Text>
          {dp.respondable && !dp.submitted ? (
            <View style={styles.btnRow}>
              {smallBtn(C.buildPacket, () => buildPacket(dp), { disabled: off })}
              {smallBtn(C.saveDraft, () => act({ disputeId: dp.id }, 'draft'), { disabled: off })}
              {smallBtn(C.submit, () => act({ disputeId: dp.id }, 'submit', C.confirmSubmit(amount)), { disabled: off })}
              {smallBtn(C.accept, () => act({ disputeId: dp.id }, 'accept', C.confirmAccept(amount)), { danger: true, disabled: off })}
            </View>
          ) : null}
        </View>
        {renderPacket(dp)}
      </View>
    );
  };

  const renderWarning = (w) => {
    const amount = fmtUsd(w.amountCents);
    const a = w.actions || warningActions(w);
    const off = busy === w.id;
    return (
      <View key={w.id} style={styles.rowWrap}>
        <View style={styles.row}>
          <Text style={styles.rowName}>{warningRowLine(w)}</Text>
          {chipRow(warningChips(w))}
          <Text style={styles.rowKey}>{w.id}</Text>
          {a.refund || a.refundFraud || a.applyHold || a.handleNow ? (
            <View style={styles.btnRow}>
              {a.handleNow ? smallBtn(C.handleNow, () => act({ efwId: w.id, ...modeArg() }, 'efw_import', C.confirmHandleNow(amount)), { disabled: off }) : null}
              {a.refund ? smallBtn(C.refund, () => act({ efwId: w.id }, 'efw_refund', C.confirmRefund(amount)), { disabled: off }) : null}
              {a.refundFraud ? smallBtn(C.refundFraud, () => act({ efwId: w.id }, 'efw_refund_fraud', C.confirmRefundFraud), { danger: true, disabled: off }) : null}
              {a.applyHold ? smallBtn(C.applyHold, () => act({ efwId: w.id }, 'apply_hold', C.confirmApplyHold), { danger: true, disabled: off }) : null}
            </View>
          ) : null}
        </View>
      </View>
    );
  };

  const noteField = (block) => (
    <>
      <Text style={styles.fieldLabel}>{C.note}</Text>
      <TextInput
        style={[styles.input, styles.noteInput]}
        value={drafts[block].note}
        onChangeText={(v) => setDraft(block, { note: v })}
        placeholderTextColor="#666"
        editable={!saving}
        multiline
      />
      <TouchableOpacity
        style={[styles.saveBtn, (saving || !String(drafts[block].note || '').trim()) && styles.btnDisabled]}
        onPress={() => saveBlock(block)}
        disabled={!!saving || !String(drafts[block].note || '').trim()}
        accessibilityRole="button"
      >
        {saving === block ? <ActivityIndicator color="#000" /> : <Text style={styles.saveBtnText}>{C.save}</Text>}
      </TouchableOpacity>
    </>
  );

  const options = (values, labels, current, onPick) => (
    <View style={styles.optionRow}>
      {values.map((v) => {
        const on = current === v;
        return (
          <TouchableOpacity
            key={v}
            style={[styles.option, on && styles.optionOn]}
            onPress={() => onPick(v)}
            disabled={!!saving}
            accessibilityRole="radio"
            accessibilityState={{ checked: on, disabled: !!saving }}
          >
            <Text style={[styles.optionText, on && styles.optionTextOn]}>{labels[v]}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const toggle = (label, value, onChange) => (
    <View style={styles.toggleRow}>
      <Text style={styles.toggleLabel}>{label}</Text>
      <Switch
        value={value}
        onValueChange={onChange}
        disabled={!!saving}
        trackColor={{ false: '#333', true: '#4CAF50' }}
        thumbColor={value ? '#fff' : '#888'}
      />
    </View>
  );

  const renderSettings = () => {
    if (!drafts && configError) {
      return (
        <>
          <Text style={styles.errorText}>{configError}</Text>
          {smallBtn('Retry', loadConfig)}
        </>
      );
    }
    if (!drafts) return <ActivityIndicator color={COLORS.GOLD} style={styles.spinner} />;
    return (
      <>
        {/* Issuer fraud warnings ({efw}; audit efw_config) */}
        <Text style={[styles.subTitle, styles.subTitleGap]}>{C.efwTitle}</Text>
        {toggle(C.efwSwitch, drafts.efw.autoRefund, (v) => setDraft('efw', { autoRefund: v }))}
        <Text style={styles.fieldLabel}>{C.efwMax}</Text>
        <TextInput
          style={styles.input}
          value={drafts.efw.maxDollars}
          onChangeText={(v) => setDraft('efw', { maxDollars: v })}
          keyboardType="decimal-pad"
          placeholder="20"
          placeholderTextColor="#666"
          editable={!saving}
        />
        <Text style={styles.hint}>{C.efwHint}</Text>
        {noteField('efw')}

        {/* 3D Secure ({threeDS}; audit three_ds_config) */}
        <Text style={[styles.subTitle, styles.subTitleGap]}>{C.threeDSTitle}</Text>
        <Text style={styles.fieldLabel}>{C.threeDSTips}</Text>
        {options(SCOPES, C.scopeLabels, drafts.threeDS.tips, (v) => setDraft('threeDS', { tips: v }))}
        {noteField('threeDS')}

        {/* Membership joins ({join}; audit join_gate_config) */}
        <Text style={[styles.subTitle, styles.subTitleGap]}>{C.joinTitle}</Text>
        {toggle(C.joinEmail, drafts.join.emailRequired, (v) => setDraft('join', { emailRequired: v }))}
        <Text style={styles.fieldLabel}>{C.joinWait}</Text>
        <TextInput
          style={styles.input}
          value={drafts.join.hours}
          onChangeText={(v) => setDraft('join', { hours: v })}
          keyboardType="number-pad"
          placeholder="0"
          placeholderTextColor="#666"
          editable={!saving}
        />
        <Text style={styles.fieldLabel}>{C.joinFlow}</Text>
        {options(FLOWS, C.flowLabels, drafts.join.flow, (v) => setDraft('join', { flow: v }))}
        {noteField('join')}
      </>
    );
  };

  // ---------------------------------------------------------------- render
  const disputes = list?.disputes || [];
  const warnings = list?.warnings || [];
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{C.title}</Text>
      {list === null && !listError ? <ActivityIndicator color={COLORS.GOLD} style={styles.spinner} /> : null}
      {listError ? (
        <>
          <Text style={styles.errorText}>{listError}</Text>
          {smallBtn('Retry', loadList)}
        </>
      ) : null}
      {list && !hasOpenItems(list) ? <Text style={styles.empty}>{C.empty}</Text> : null}
      {disputes.map(renderDispute)}
      {warnings.map(renderWarning)}
      {renderSettings()}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: COLORS.CARD, borderRadius: 12, padding: 16, marginBottom: 16,
    borderWidth: 1, borderColor: COLORS.BORDER,
  },
  sectionTitle: {
    color: COLORS.GOLD, fontSize: 14, fontWeight: '700', marginBottom: 12,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  spinner: { marginTop: 12 },
  errorText: { color: COLORS.DANGER, fontSize: 13, marginTop: 8 },
  warnText: { color: COLORS.WARN, fontSize: 12.5, marginTop: 8, lineHeight: 17 },
  hint: { color: COLORS.DIM, fontSize: 12, marginTop: 6, lineHeight: 17 },
  empty: { color: '#666', fontSize: 13, marginTop: 4, fontStyle: 'italic' },
  rowWrap: { marginTop: 10 },
  row: { padding: 12, borderRadius: 8, borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515' },
  rowName: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700' },
  rowMeta: { color: '#aaa', fontSize: 12, marginTop: 4 },
  rowKey: {
    color: '#555', fontSize: 10.5, marginTop: 4,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  chip: {
    color: COLORS.GOLD, fontSize: 11.5, fontWeight: '700', borderWidth: 1, borderColor: '#5a4a00',
    borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2, overflow: 'hidden',
  },
  btnRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  smallBtn: { paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, backgroundColor: COLORS.GOLD, alignItems: 'center' },
  smallBtnText: { color: '#000', fontSize: 12.5, fontWeight: '700' },
  smallBtnDanger: {
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1,
    borderColor: COLORS.DANGER, alignItems: 'center',
  },
  smallBtnDangerText: { color: COLORS.DANGER, fontSize: 12.5, fontWeight: '700' },
  btnDisabled: { opacity: 0.5 },
  packet: { marginLeft: 12, paddingLeft: 10, borderLeftWidth: 2, borderLeftColor: '#2a2a2a', marginTop: 6 },
  packetField: { marginTop: 6 },
  packetKey: { color: COLORS.DIM, fontSize: 11.5, fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace' },
  packetVal: { color: '#ddd', fontSize: 12.5, lineHeight: 18, marginTop: 2 },
  subTitle: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700' },
  subTitleGap: { marginTop: 22 },
  toggleRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: COLORS.BORDER,
  },
  toggleLabel: { color: COLORS.TEXT, fontSize: 14, flex: 1, marginRight: 12 },
  fieldLabel: { color: COLORS.DIM, fontSize: 12, marginBottom: 6, marginTop: 10 },
  input: {
    backgroundColor: '#1a1a1a', borderRadius: 8, padding: 12, color: COLORS.TEXT,
    fontSize: 16, borderWidth: 1, borderColor: '#333',
  },
  noteInput: { minHeight: 56, fontSize: 14 },
  saveBtn: { backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 12, alignItems: 'center', marginTop: 12 },
  saveBtnText: { color: '#000', fontSize: 14, fontWeight: '800' },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  option: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: '#2a2a2a' },
  optionOn: { borderColor: COLORS.GOLD, backgroundColor: 'rgba(252, 208, 0, 0.06)' },
  optionText: { color: '#ccc', fontSize: 13 },
  optionTextOn: { color: COLORS.TEXT, fontWeight: '700' },
});
